/**
 * app.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 * Test Automatic Deployment
 */

// Import all the third party stuff
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import history from 'utils/history'
import 'sanitize.css/sanitize.css'
import 'bootstrap/dist/css/bootstrap.min.css'

// Import root app
import App from './containers/App/App'

import './global.scss'
import './fonts/fonts.scss'

// Import Language Provider
import LanguageProvider from 'containers/LanguageProvider'

// Load the favicon and the .htaccess file
/* eslint-disable import/no-unresolved, import/extensions */
import '!file-loader?name=[name].[ext]!./images/logox.svg'
import 'file-loader?name=.htaccess!./.htaccess'
/* eslint-enable import/no-unresolved, import/extensions */

import { Router } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Import i18n messages
import { translationMessages } from './i18n'
import store from './store'
import { primeCurrentUser } from './store/global'
import { ConfigProvider } from 'antd'
const DEFAULT_STALE_TIME = 5 * 60 * 1000 // 5 minutes
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: DEFAULT_STALE_TIME,
    },
  },
})

declare global {
  interface Window {
    _env_: {
      API_URL: string
      SCROLL_LIMIT: string
      NODE_ENV: string
    }
    Intl?: typeof Intl
  }
}

// Kick off the auth request before React mounts (IVX-9754) so it runs while the
// app renders instead of starting from a useEffect after first paint.
primeCurrentUser()

const MOUNT_NODE = document.getElementById('app')
if (!MOUNT_NODE) {
  throw new Error('Failed to find the root element')
}

const root = createRoot(MOUNT_NODE)

const render = (messages: Record<string, string>): void => {
  const AppWithProviders = () => (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <Router history={history}>
          <LanguageProvider messages={messages}>
            <ConfigProvider
              theme={{
                token: {
                  colorPrimary: 'rgba(24, 144, 255, 1)',
                  borderRadius: 2,
                },
              }}
            >
              <App />
            </ConfigProvider>
          </LanguageProvider>
        </Router>
      </Provider>
    </QueryClientProvider>
  )

  root.render(<AppWithProviders />)
}

// Chunked polyfill for browsers without Intl support
if (!window.Intl) {
  Promise.all([import('intl'), import('intl/locale-data/jsonp/en.js')])
    .then(() => render(translationMessages))
    .catch(err => {
      throw err
    })
} else {
  render(translationMessages)
}

if (module.hot) {
  module.hot.accept(['containers/App'], () => {
    const NextApp = require('containers/App').default
    root.render(
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <Router history={history}>
            <LanguageProvider messages={translationMessages}>
              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: 'rgba(24, 144, 255, 1)',
                    borderRadius: 2,
                  },
                }}
              >
                <NextApp />
              </ConfigProvider>
            </LanguageProvider>
          </Router>
        </Provider>
      </QueryClientProvider>,
    )
  })
}
